// @docs:
//   - docs/core/operations/OTA_CHANGELOG.md
// Admin › Changelog. Two tabs: Client, every OTA sent to Scouts since release
// notes began, newest first: per app, what Scouts were shown and what
// changed; and Platform, every deploy (changelog/PlatformTab.tsx). Copyable
// as plain text or Markdown. Read-only.
//
// What is open lives in the page address (data/changelog.ts readView), so a
// reload, the back button or a copied link lands in the same place.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChangelogHeader, TabSwitch, type Repo } from './changelog/Header';
import { RegisterContext } from './changelog/KbChanges';
import { LiveApps } from './changelog/LiveApps';
import { PlatformTab } from './changelog/PlatformTab';
import { LoadingTimeline, Notice, RetryButton, TextButton } from './changelog/parts';
import { Pending } from './changelog/Pending';
import { SearchResults } from './changelog/SearchResults';
import { UpdateCard, type CopyFormat } from './changelog/UpdateCard';
import {
  PENDING_ID,
  SEARCH_MIN,
  appNames,
  byMonth,
  nzSince as since,
  readView,
  searchChanges,
  updateId,
  useOtaChangelog,
  writeView,
  type ChangelogOta,
  type ViewState,
} from './data/changelog';

const FORMAT_KEY = 'ota-changelog-copy-format';

function readFormat(): CopyFormat {
  try {
    return localStorage.getItem(FORMAT_KEY) === 'markdown' ? 'markdown' : 'text';
  } catch {
    return 'text';
  }
}

/** Client and Platform share the page; the address says which (repo=platform). */
export function OtaChangelogPage() {
  const [params, setParams] = useSearchParams();
  const [format, setFormat] = useState<CopyFormat>(readFormat);
  const repo: Repo = params.get('repo') === 'platform' ? 'platform' : 'client';
  const pickFormat = (f: CopyFormat) => {
    setFormat(f);
    try {
      localStorage.setItem(FORMAT_KEY, f);
    } catch {
      // private window: the choice lasts for this visit
    }
  };
  // Each tab keeps its own place; switching starts the other tab fresh.
  const onRepo = (r: Repo) => { if (r !== repo) setParams(r === 'platform' ? new URLSearchParams({ repo: 'platform' }) : new URLSearchParams()); };
  return repo === 'platform'
    ? <PlatformTab format={format} onFormat={pickFormat} onRepo={onRepo} />
    : <ClientChangelog format={format} onFormat={pickFormat} onRepo={onRepo} />;
}

function ClientChangelog({ format, onFormat, onRepo }: { format: CopyFormat; onFormat: (f: CopyFormat) => void; onRepo: (r: Repo) => void }) {
  const { data, isLoading, isError, refetch, isFetching } = useOtaChangelog();
  const [params, setParams] = useSearchParams();
  const [now] = useState(() => Date.now());
  const otas = useMemo(() => data?.otas ?? [], [data]);
  const newest = otas[0];
  const view = useMemo(() => readView(params, newest), [params, newest]);

  const change = (edit: (v: ViewState) => void) => {
    const next: ViewState = { open: new Set(view.open), apps: new Set(view.apps), only: new Set(view.only), q: view.q, tab: view.tab };
    edit(next);
    setParams(writeView(next), { replace: true });
  };

  // Scroll to an update named in the address (a copied link, or "Open this
  // update" from search) once it is on the page.
  const [scrollTo, setScrollTo] = useState<string | null>(() => (params.has('open') && params.get('open')?.split(',').length === 1 ? params.get('open') : null));
  useEffect(() => {
    if (!scrollTo || !data) return;
    const el = document.getElementById(scrollTo.startsWith(`${PENDING_ID}-`) ? scrollTo : `update-${scrollTo}`);
    if (el) el.scrollIntoView({ block: 'start' });
    setScrollTo(null);
  }, [scrollTo, data]);

  // Apps chosen in "On Scouts now": only updates with one of them, showing only them.
  const only = view.only;
  const chosen = useMemo(() => (only.size ? (a: { app: string }) => only.has(a.app) : () => true), [only]);
  const visible = useMemo(() => otas.filter((o) => o.apps.some(chosen)), [otas, chosen]);
  const appsOf = (o: ChangelogOta) => o.apps.filter(chosen);
  const allPending = useMemo(() => data?.pending ?? [], [data]);
  const pending = useMemo(() => allPending.filter(chosen), [allPending, chosen]);
  const searching = view.q.trim().length >= SEARCH_MIN;
  const results = useMemo(
    () => (searching ? searchChanges(visible.map((o) => ({ ...o, apps: o.apps.filter(chosen) })), view.q, pending) : []),
    [searching, visible, chosen, view.q, pending],
  );
  const filterable = useMemo(() => new Set([...otas.flatMap((o) => o.apps.map((a) => a.app)), ...allPending.map((a) => a.app)]), [otas, allPending]);
  const next = useMemo(() => Object.fromEntries(allPending.map((a) => [a.app, a.toVersion])), [allPending]);
  const showTab = (tab: ViewState['tab']) => change((v) => {
    v.tab = tab;
    // The first look at "Not sent yet" opens its first app, as the timeline opens its newest update.
    if (tab === 'pending' && pending[0] && !pending.some((a) => v.apps.has(`${PENDING_ID}.${a.app}`))) v.apps.add(`${PENDING_ID}.${pending[0].app}`);
  });
  // Named in the tiles' order, whatever order they were chosen in.
  const chosenNames = useMemo(() => {
    const known = [...(data?.liveApps ?? []), ...otas.flatMap((o) => o.apps), ...allPending];
    const order = [...new Set(known.map((a) => a.app))].filter((app) => only.has(app));
    const rest = [...only].filter((app) => !order.includes(app));
    return [...order, ...rest].map((app) => ({ name: known.find((a) => a.app === app)?.name ?? app }));
  }, [data, otas, allPending, only]);
  const filterName = appNames(chosenNames);

  const linkTo = (o: ChangelogOta) => () => {
    const id = updateId(o.firstOfferedAt);
    const apps = [...view.apps].filter((k) => k.startsWith(`${id}.`));
    const p = writeView({ open: new Set([id]), apps: new Set(apps.length ? apps : [`${id}.${o.apps[0]?.app}`]), only: new Set(), q: '', tab: 'sent' });
    return `${window.location.origin}${window.location.pathname}?${p.toString()}`;
  };

  return (
    <RegisterContext.Provider value={data?.register ?? null}>
    <div className="max-w-[1120px] space-y-6 px-4 pb-16 pt-6 sm:px-6 lg:px-10 lg:pt-7">
      <ChangelogHeader repo="client" onRepo={onRepo} q={view.q} onQ={(q) => change((v) => { v.q = q; })} format={format} onFormat={onFormat} />

      {data?.github === 'not_configured' && (
        <Notice tone="warning">
          <strong className="font-semibold text-white">Release notes aren't connected.</strong> Every update is listed, but its notes can't be read from GitHub until the API has its GitHub app settings.
        </Notice>
      )}
      {data?.github === 'error' && (
        <Notice tone="error" action={<RetryButton onClick={() => void refetch()} busy={isFetching} />}>
          <strong className="font-semibold text-white">Couldn't load release notes from GitHub.</strong> Updates are listed without their notes.
          {data.githubError && <span className="text-slate-400"> ({data.githubError})</span>}
        </Notice>
      )}
      {isError && (
        <Notice tone="error" action={<RetryButton onClick={() => void refetch()} busy={isFetching} />}>
          <strong className="font-semibold text-white">Couldn't load the changelog.</strong> Check your connection and try again.
        </Notice>
      )}

      {isLoading && <LoadingTimeline />}

      {data && (
        <>
          <LiveApps
            apps={data.liveApps ?? []}
            next={next}
            chosen={only}
            filterable={filterable}
            busiest={data.busiest}
            onPick={(app) => change((v) => { if (v.only.has(app)) v.only.delete(app); else v.only.add(app); })}
          />

          {only.size > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-cyan-400/25 bg-cyan-400/[0.07] py-1.5 pl-4 pr-1.5 text-[15px] text-slate-200">
              <span className="min-w-0 flex-1 py-1">
                Showing <strong className="font-semibold text-white">{filterName}</strong> only · {view.tab === 'pending'
                  ? (pending.length ? 'changes waiting to go out' : 'nothing waiting to go out')
                  : `${visible.length} ${visible.length === 1 ? 'update' : 'updates'}`}
              </span>
              <button type="button" onClick={() => change((v) => { v.only = new Set(); })} className="min-h-[36px] rounded-md px-2.5 text-sm font-medium text-cyan-300 hover:bg-slate-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500">
                Show every app
              </button>
            </div>
          )}

          {searching ? (
            <SearchResults
              q={view.q}
              groups={results}
              onOpen={(g) => {
                if (g.updateId === PENDING_ID) {
                  change((v) => { v.q = ''; v.tab = 'pending'; v.apps.add(`${PENDING_ID}.${g.app}`); });
                  setScrollTo(`${PENDING_ID}-${g.app}`);
                  return;
                }
                change((v) => { v.q = ''; v.tab = 'sent'; v.open.add(g.updateId); v.apps.add(`${g.updateId}.${g.app}`); });
                setScrollTo(g.updateId);
              }}
            />
          ) : (
            <>
              <TabSwitch tab={view.tab} sent={visible.length} pending={pending.length} onPick={showTab} />
            </>
          )}

          {!searching && view.tab === 'pending' && (
            <Pending
              apps={pending}
              prAccess={data.prAccess ?? 'unavailable'}
              isOpen={(app) => view.apps.has(`${PENDING_ID}.${app}`)}
              onToggle={(app) => change((v) => { const k = `${PENDING_ID}.${app}`; if (v.apps.has(k)) v.apps.delete(k); else v.apps.add(k); })}
              format={format}
              filtered={only.size}
            />
          )}

          {!searching && view.tab === 'sent' && (
            <>
              <div className="-mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-slate-400">
                  {visible.length} {visible.length === 1 ? 'update' : 'updates'}
                  {otas.length > 0 && <> since {since(otas[otas.length - 1].firstOfferedAt)}</>} · Times are New Zealand time
                </span>
                {visible.length > 0 && (
                  <span className="flex gap-1">
                    <TextButton onClick={() => change((v) => {
                      v.open = new Set(visible.map((o) => updateId(o.firstOfferedAt)));
                      v.apps = new Set(visible.flatMap((o) => appsOf(o).map((a) => `${updateId(o.firstOfferedAt)}.${a.app}`)));
                    })}>Expand all</TextButton>
                    <TextButton onClick={() => change((v) => { v.open = new Set(); v.apps = new Set(); })}>Collapse all</TextButton>
                  </span>
                )}
              </div>

              {visible.length === 0 && (
                <p className="text-[15px] text-slate-400">
                  {only.size ? `No updates to ${appNames(chosenNames, 'or')} are listed here.` : `No updates have been sent since release notes began.`}
                </p>
              )}

              {byMonth(visible).map((m) => (
                <section key={m.label} aria-labelledby={`month-${m.label}`} className="space-y-3">
                  <h2 id={`month-${m.label}`} className="flex items-baseline gap-2.5 text-[15px] font-semibold text-slate-200">
                    {m.label} <span className="text-sm font-medium text-slate-500">{m.otas.length} {m.otas.length === 1 ? 'update' : 'updates'}</span>
                  </h2>
                  {m.otas.map((o) => {
                    const id = updateId(o.firstOfferedAt);
                    return (
                      <UpdateCard
                        key={id}
                        ota={o}
                        id={id}
                        apps={appsOf(o)}
                        latest={o === newest}
                        open={view.open.has(id)}
                        isAppOpen={(app) => view.apps.has(`${id}.${app}`)}
                        onToggle={() => change((v) => {
                          if (v.open.has(id)) { v.open.delete(id); return; }
                          v.open.add(id);
                          // Opening an update with nothing open inside it opens its first app, so one click shows something.
                          const shown = appsOf(o);
                          if (shown[0] && !shown.some((a) => v.apps.has(`${id}.${a.app}`))) v.apps.add(`${id}.${shown[0].app}`);
                        })}
                        onToggleApp={(app) => change((v) => { const k = `${id}.${app}`; if (v.apps.has(k)) v.apps.delete(k); else v.apps.add(k); })}
                        linkTo={linkTo(o)}
                        format={format}
                        now={now}
                      />
                    );
                  })}
                </section>
              ))}
            </>
          )}
        </>
      )}
    </div>
    </RegisterContext.Provider>
  );
}
